import { formatUTCDateToISODateString } from '@fruitful/fruitils';
import { addMonths, format, startOfMonth, subMonths } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H2Regular,
    H6Emphasized,
    SmoothieScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import { syncteraPostedTransactionUrl } from '../../common/utils/LinkUtils';
import { requestBuilder } from '../../http/RequestBuilder';

const monthNameDisplay = (date: Date) =>
    date.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
    });

export const BankOpsDirectDepositPanel = () => {
    const defaultDate = startOfMonth(subMonths(new Date(), 2));
    const [directDepositDate, setDirectDepositDate] =
        useState<Date>(defaultDate);

    const handleDateChange = (value: Date | null | undefined): void => {
        if (value) {
            setDirectDepositDate(value);
        }
    };

    return (
        <YStack gap={16}>
            <XStack
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                role="banner"
            >
                <H6Emphasized>
                    Direct Deposit Review - Incoming ACH{' '}
                    {monthNameDisplay(directDepositDate)} - Applies to card
                    spend {monthNameDisplay(addMonths(directDepositDate, 1))}
                </H6Emphasized>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>Month:</BodyMediumRegular>
                    <Calendar
                        value={directDepositDate}
                        onChange={(e) => {
                            handleDateChange(e.value as Date);
                        }}
                        showIcon
                        dateFormat="mm/yy"
                        view="month"
                        yearNavigator
                        yearRange="2020:2030"
                        aria-label="Select month for direct deposit transactions"
                        maxDate={startOfMonth(new Date())}
                    />
                </XStack>
            </XStack>
            <IncomingAchByMembershipTable
                directDepositMonth={directDepositDate}
            />
            <ReconButtonAndPopup directDepositMonth={directDepositDate} />
        </YStack>
    );
};

interface PostedIncomingAchTransaction {
    id: string;
    transactionUuid: string;
    syncteraAccountId: string;
    membershipId: string;
    postedDate: Date;
    amountCents: number;
    dcSign: 'credit' | 'debit';
    type: string;
    subtype: string;
    sourceCompanyName: string;
    sourceAccountName: string;
    description: string | null;
    secCode: string | null;
    isDirectDeposit: boolean;
    createdAt: Date;
    updatedAt: Date | null;
}

interface IncomingAchByMembership {
    membershipId: string;
    primaryMemberId: string;
    primaryMemberName: string;
    jointMemberId?: string;
    jointMemberName?: string;
    sumQualifyingIncomingAchTransactions: number;
    sumAllIncomingAchTransactions: number;
    incomingAchTransactions: PostedIncomingAchTransaction[];
}

interface AdminBankOpsGetIncomingAchTransactionsResponseDto {
    incomingAchTransactionsByMembership: IncomingAchByMembership[];
    numMembershipsQualifyingTwoPercentCashBack: number;
    numMembersQualifyingTwoPercentCashBack: number;
    numMembershipsWithDirectDeposit: number;
    numMembersWithDirectDeposit: number;
}

const useGetIncomingAchTransactions = requestBuilder.query<
    AdminBankOpsGetIncomingAchTransactionsResponseDto,
    { date: string },
    Error
>({
    endpoint: ({ date }) =>
        `/api/admin/bank-ops/direct-deposit/incoming-ach-transactions/${date}`,
    queryKey: ({ date }) =>
        `/api/admin/bank-ops/direct-deposit/incoming-ach-transactions/${date}`,
});

interface FilterDropdownElement {
    name: string;
    code: string;
}

const IncomingAchByMembershipTable = ({
    directDepositMonth,
}: {
    directDepositMonth: Date;
}) => {
    const [selectedFilter, setSelectedFilter] = useState<FilterDropdownElement>(
        { name: 'Show all', code: 'ALL' },
    );
    const directDepositFilterRules: FilterDropdownElement[] = [
        { name: 'Show all', code: 'ALL' },
        { name: 'Hide SEC code WEB', code: 'HIDE_WEB' },
        { name: 'Is DD = Y', code: 'Y_ONLY' },
        { name: 'Is DD = N', code: 'N_ONLY' },
    ];
    const [
        filteredIncomingAchTransactionsByMembership,
        setFilteredIncomingAchTransactionsByMembership,
    ] = useState<IncomingAchByMembership[] | null>(null);

    const filterRuleFilterFunction = (
        code: string,
    ): ((x: PostedIncomingAchTransaction) => boolean) => {
        if (code === 'ALL') {
            return (_: PostedIncomingAchTransaction) => true;
        } else if (code === 'HIDE_WEB') {
            return (x: PostedIncomingAchTransaction) => x.secCode !== 'WEB';
        } else if (code === 'Y_ONLY') {
            return (x: PostedIncomingAchTransaction) => x.isDirectDeposit;
        } else if (code === 'N_ONLY') {
            return (x: PostedIncomingAchTransaction) => !x.isDirectDeposit;
        } else {
            throw new Error('unexpected case in filterRuleFilterFunction');
        }
    };
    const applySelectedFilterToAllIncomingAchTransctionsByMembership = (
        code: string,
    ) => {
        if (incomingAchTransactions == null) {
            return;
        }
        const filterFunction = filterRuleFilterFunction(code);

        const result: IncomingAchByMembership[] = [];
        for (const a of incomingAchTransactions.incomingAchTransactionsByMembership) {
            const element = {
                ...a,
                incomingAchTransactions: a.incomingAchTransactions.filter((x) =>
                    filterFunction(x),
                ),
            };
            result.push(element);
        }
        setFilteredIncomingAchTransactionsByMembership(result);
    };

    const {
        data: incomingAchTransactions,
        isLoading: isLoadingIncomingAchTransactions,
    } = useGetIncomingAchTransactions({
        date: format(directDepositMonth, 'yyyy-MM-dd'),
    });

    if (isLoadingIncomingAchTransactions || incomingAchTransactions == null) {
        return <Spinner />;
    }

    const incomingAchTransactionsByMembership =
        incomingAchTransactions.incomingAchTransactionsByMembership;
    if (incomingAchTransactionsByMembership.length === 0) {
        return (
            <BodyMediumRegular>
                No direct deposit transactions found
            </BodyMediumRegular>
        );
    }

    return (
        <>
            <XStack gap={8} justifyContent="space-evenly" width="100%">
                <YStack>
                    <BodyLargeEmphasized>
                        2% Cash Back Qualifying
                    </BodyLargeEmphasized>
                    <BodySmallRegular marginVertical={16}>
                        {
                            incomingAchTransactions.numMembershipsQualifyingTwoPercentCashBack
                        }{' '}
                        memberships
                        <br />
                        {
                            incomingAchTransactions.numMembersQualifyingTwoPercentCashBack
                        }{' '}
                        members
                        <br />
                    </BodySmallRegular>
                </YStack>
                <YStack>
                    <BodyLargeEmphasized>
                        Has Direct Deposit
                    </BodyLargeEmphasized>
                    <BodySmallRegular marginVertical={16}>
                        {
                            incomingAchTransactions.numMembershipsWithDirectDeposit
                        }{' '}
                        memberships
                        <br />
                        {
                            incomingAchTransactions.numMembersWithDirectDeposit
                        }{' '}
                        members
                        <br />
                    </BodySmallRegular>
                </YStack>
            </XStack>
            <YStack gap={8}>
                <Dropdown
                    value={selectedFilter}
                    onChange={(e) => {
                        const selectedDropdownElement =
                            e.value as FilterDropdownElement;
                        setSelectedFilter(selectedDropdownElement);
                        applySelectedFilterToAllIncomingAchTransctionsByMembership(
                            selectedDropdownElement.code,
                        );
                    }}
                    options={directDepositFilterRules}
                    optionLabel="name"
                    placeholder="Filter incoming ACH transactions"
                />
                <DataTable
                    dataKey="membershipId"
                    value={
                        filteredIncomingAchTransactionsByMembership ??
                        incomingAchTransactionsByMembership
                    }
                    size="small"
                    tableStyle={{ width: '100%' }}
                >
                    <Column
                        header="Row"
                        style={{ width: '4rem', verticalAlign: 'top' }}
                        body={(_, options) => {
                            return <>{options.rowIndex + 1}</>;
                        }}
                    />
                    <Column
                        field="membershipId"
                        header="MembershipId"
                        style={{ width: '4rem', verticalAlign: 'top' }}
                        body={(x: IncomingAchByMembership) => (
                            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                                MembershipId: {x.membershipId}
                                <br />
                                Primary member: {x.primaryMemberName}
                                <br />
                                {x.jointMemberId != null && (
                                    <>
                                        Joint member: {x.jointMemberName}
                                        <br />
                                    </>
                                )}
                                <br />
                                Sum qualifying:{' '}
                                {x.sumQualifyingIncomingAchTransactions >=
                                200000 ? (
                                    <BodySmallEmphasized color="$green900">
                                        {centsToFormattedDollars(
                                            x.sumQualifyingIncomingAchTransactions,
                                        )}
                                    </BodySmallEmphasized>
                                ) : (
                                    <>
                                        {centsToFormattedDollars(
                                            x.sumQualifyingIncomingAchTransactions,
                                        )}
                                    </>
                                )}
                                <br />
                                Sum all:{' '}
                                {centsToFormattedDollars(
                                    x.sumAllIncomingAchTransactions,
                                )}
                                <br />
                            </BodySmallRegular>
                        )}
                    />
                    <Column
                        style={{ verticalAlign: 'top' }}
                        body={RenderIncomingAchByMembership}
                    />
                </DataTable>
            </YStack>
        </>
    );
};

const RenderIncomingAchByMembership = (
    incomingAchByMembership: IncomingAchByMembership,
) => {
    return (
        <YStack>
            <DataTable
                key={incomingAchByMembership.membershipId}
                value={incomingAchByMembership.incomingAchTransactions}
                size="small"
                tableStyle={{ width: '100%' }}
                emptyMessage="All rows filtered out"
            >
                <Column
                    field="transactionUuid"
                    header="Uuid"
                    body={(x: PostedIncomingAchTransaction) => (
                        <a
                            href={syncteraPostedTransactionUrl(
                                x.transactionUuid,
                            )}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {x.transactionUuid.substring(0, 3)}..
                            {x.transactionUuid.slice(-3)}
                        </a>
                    )}
                />
                <Column
                    field="isDirectDeposit"
                    header="Is DD"
                    body={(x: PostedIncomingAchTransaction) => (
                        <>
                            {x.isDirectDeposit ? (
                                <BodySmallEmphasized color="$green900">
                                    Y
                                </BodySmallEmphasized>
                            ) : (
                                <BodySmallRegular color="$red900">
                                    N
                                </BodySmallRegular>
                            )}
                        </>
                    )}
                />
                <Column
                    field="postedDate"
                    header="Posted Date"
                    body={(x: PostedIncomingAchTransaction) => (
                        <>{formatUTCDateToISODateString(x.postedDate)}</>
                    )}
                />
                <Column field="subtype" header="Subtype" />
                <Column
                    field="amountCents"
                    header="Amount"
                    body={(x: PostedIncomingAchTransaction) => (
                        <>{centsToFormattedDollars(x.amountCents)}</>
                    )}
                />
                <Column field="secCode" header="SEC code" />
                <Column field="description" header="Description" />
                <Column field="sourceCompanyName" header="Company name" />
            </DataTable>
        </YStack>
    );
};

interface AdminBankOpsGetIncomingAchReconResponseDto {
    incomingAchTransactionsRecon: object;
}

const useGetIncomingAchRecon = requestBuilder.query<
    AdminBankOpsGetIncomingAchReconResponseDto,
    { date: string },
    Error
>({
    endpoint: ({ date }) =>
        `/api/admin/bank-ops/direct-deposit/incoming-ach-recon/${date}`,
    queryKey: ({ date }) =>
        `/api/admin/bank-ops/direct-deposit/incoming-ach-recon/${date}`,
});

const ReconButtonAndPopup = ({
    directDepositMonth,
}: {
    directDepositMonth: Date;
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const monthDisplay = directDepositMonth.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
    });

    return (
        <>
            <Button
                variant="primary"
                width="auto"
                size="small"
                onPress={() => {
                    setIsOpen(true);
                }}
            >
                {/* Button label in string as a type hack */}
                {`Run Recon for ${monthDisplay}`}
            </Button>
            <GroveSimpleModal
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
            >
                <SmoothieScrollView maxHeight="80vh">
                    <YStack
                        marginHorizontal={16}
                        marginTop={32}
                        width="90vw"
                        maxWidth={1000}
                    >
                        <H2Regular>
                            {monthDisplay} direct deposit ACH reconcilation
                            checks
                        </H2Regular>
                        <BodySmallRegular marginVertical={16}>
                            If data does not reconcile, discuss with Bob /
                            action team.
                        </BodySmallRegular>
                        <ReconComponent
                            directDepositMonth={directDepositMonth}
                        />
                    </YStack>
                </SmoothieScrollView>
            </GroveSimpleModal>
        </>
    );
};

const ReconComponent = ({
    directDepositMonth,
}: {
    directDepositMonth: Date;
}) => {
    const { data: incomingAchRecon, isLoading: isLoadingIncomingAchRecon } =
        useGetIncomingAchRecon({
            date: format(directDepositMonth, 'yyyy-MM-dd'),
        });

    return (
        <YStack
            gap={12}
            backgroundColor="$gray100"
            padding={16}
            borderRadius={8}
            role="region"
            aria-label="Incoming ACH Recon Control"
        >
            {isLoadingIncomingAchRecon ? (
                <Spinner />
            ) : (
                <>
                    <H6Emphasized>Incoming ACH recon</H6Emphasized>
                    <BodyMediumRegular>
                        {JSON.stringify(
                            incomingAchRecon?.incomingAchTransactionsRecon,
                            null,
                            2,
                        )}
                    </BodyMediumRegular>
                </>
            )}
        </YStack>
    );
};
